import { Seo } from '@frontend/shared/web';
import { zodResolver } from '@hookform/resolvers/zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AllocabLogo } from '../../components/layout/AllocabLogo';
import { useExtranetLogin } from '../../services/api/hooks/useExtranetLogin';

const schema = z.object({
  email: z.string().trim().email({ message: 'Email invalide' }),
  password: z.string().trim().min(1, { message: 'Mot de passe requis' }),
});

type LoginFormValues = z.infer<typeof schema>;

export const LoginRoute = () => {
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = form;

  const { mutate: login, isPending, error } = useExtranetLogin();

  const onSubmit: SubmitHandler<LoginFormValues> = ({ email, password }) => {
    login({ email, password });
  };

  return (
    <>
      <Seo title="Connexion — Portail extranet" />

      <main className="min-h-viewport flex flex-col items-center justify-center bg-zinc-50 px-4">
        <div className="w-full max-w-[380px] flex flex-col gap-7 py-12">
          <header className="flex flex-col gap-3 items-center text-center">
            <AllocabLogo className="h-6 w-auto text-zinc-900" />
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 mt-2">Portail extranet</h1>
            <p className="text-sm text-zinc-500 leading-relaxed">
              Connectez-vous pour piloter vos prestations de transport.
            </p>
          </header>

          <form
            className="flex flex-col gap-3 bg-white border border-zinc-200 rounded-lg p-6 shadow-sm"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Field
              id="email"
              type="email"
              label="Email"
              placeholder="jean.dupont@allocab.com"
              autoComplete="email"
              {...register('email')}
              error={errors.email?.message}
            />
            <Field
              id="password"
              type="password"
              label="Mot de passe"
              placeholder="••••••••"
              autoComplete="current-password"
              {...register('password')}
              error={errors.password?.message}
            />

            {error ? (
              <p
                className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 mt-1"
                role="alert"
              >
                Identifiants invalides ou compte sans accès au portail.
              </p>
            ) : null}

            <button
              type="submit"
              disabled={isPending}
              className="h-10 w-full rounded-md bg-zinc-900 px-4 text-sm font-medium text-white transition-colors duration-100 hover:bg-zinc-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 mt-2"
            >
              {isPending ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>

          <p className="text-xs text-zinc-500 text-center">
            Besoin d&apos;aide ? Contactez votre administrateur Allocab.
          </p>
        </div>
      </main>
    </>
  );
};

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}

const Field = ({ id, label, error, className, ...rest }: FieldProps) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-xs font-medium text-zinc-700">
      {label}
    </label>
    <input
      id={id}
      className={[
        'h-10 w-full px-3 text-sm bg-white border rounded-md text-zinc-900 placeholder:text-zinc-400 transition-colors',
        'focus:outline-none focus:ring-2',
        error
          ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
          : 'border-zinc-300 focus:border-zinc-400 focus:ring-zinc-900/10',
        className ?? '',
      ].join(' ')}
      {...rest}
    />
    {error ? <p className="text-xs text-red-600">{error}</p> : null}
  </div>
);
